
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { toErrorMessage } from '@/utils/errorHandling';
import { formatJobTimestamp } from '@/utils/bulkJob';
import ReplicateMenusStartForm from './ReplicateMenusStartForm.vue';
import ReplicationJobMeta from './ReplicationJobMeta.vue';
import BulkJobProgress from '@/components/shared/bulk-job/BulkJobProgress.vue';
import BulkJobHistory from '@/components/shared/bulk-job/BulkJobHistory.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import Toast from '@/utils/toast';

const namespace: string = 'menuReplication';

@Component<ReplicateMenus>({
	components: {
		ReplicateMenusStartForm,
		ReplicationJobMeta,
		BulkJobProgress,
		BulkJobHistory,
		TextButton
	}
})
export default class ReplicateMenus extends Vue {
	@Action('fetchActiveJob', { namespace }) private fetchActiveJob!: () => Promise<void>;
	@Action('fetchViewedJob', { namespace }) private fetchViewedJob!: (jobId: string) => Promise<void>;
	@Action('fetchJobHistory', { namespace }) private fetchJobHistory!: (params?: TableFilterParams) => Promise<void>;
	@Action('cancelJob', { namespace }) private cancelJob!: (jobId: string) => Promise<void>;
	@Action('stopPolling', { namespace }) private stopPolling!: () => void;
	@Action('clearViewedJob', { namespace }) private clearViewedJobAction!: () => void;
	@Action('setTenant', { namespace: 'auth' }) private setTenant!: (tenantId: string) => Promise<void>;
	@Getter('getActiveJob', { namespace }) private activeJob!: BulkJob | null;
	@Getter('getViewedJob', { namespace }) private viewedJob!: BulkJob | null;
	@Getter('hasActiveJob', { namespace }) private hasActiveJob!: boolean;
	@Getter('getJobHistory', { namespace }) private jobHistory!: BulkJob[];
	@Getter('getJobHistoryCount', { namespace }) private jobHistoryCount!: number;
	@Getter('getJobHistoryFilters', { namespace }) private historyFilters!: TableFilterParams;

	private prefillRestaurantIds: string[] = [];
	private isInitializing: boolean = true;
	private isLoadingHistory: boolean = false;
	private isCancelling: boolean = false;

	private get viewedJobTitle(): string {
		if (!this.viewedJob) {
			return '';
		}
		return this.$t('menu.replicate.viewing_title', { time: formatJobTimestamp(this.viewedJob.startedAt) });
	}

	private get totalHistoryPages(): number {
		const limit: number = this.historyFilters.limit || 10;
		return Math.max(1, Math.ceil(this.jobHistoryCount / limit));
	}

	private async created(): Promise<void> {
		this.$emit('loading', true);
		try {
			await this.fetchActiveJob();
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isInitializing = false;
			this.$emit('loading', false);
		}
	}

	private beforeDestroy(): void {
		this.stopPolling();
		this.clearViewedJobAction();
	}

	private async viewHistoryJob(jobId: string): Promise<void> {
		try {
			await this.fetchViewedJob(jobId);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	private clearViewedJob(): void {
		this.clearViewedJobAction();
	}

	private async onCancel(jobId: string): Promise<void> {
		this.isCancelling = true;
		try {
			await this.cancelJob(jobId);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isCancelling = false;
		}
	}

	/**
	 * Open a target location's menus. Stop polling before switching tenants so no stale poll
	 * requests fire against the new tenant's axios context, then navigate to the Edit-menus tab.
	 * The Tabs component reacts to the route hash, so this lands the user on that location's menus
	 * instead of leaving them on the Replicate tab.
	 *
	 * @param {string} restaurantId
	 * @return {Promise<void>}
	 */
	private async onOpenLocation(restaurantId: string): Promise<void> {
		this.stopPolling();
		try {
			await this.setTenant(restaurantId);
			this.$router.push('/menus#edit-menus').catch(() => {});
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	/**
	 * Pre-fill the start form with the failed target locations from a finished job so the user can
	 * re-run them. Menus are not persisted on the job, so the user re-selects which menus to copy.
	 *
	 * @param {string[]} restaurantIds
	 * @return {void}
	 */
	private onRetryFailed(restaurantIds: string[]): void {
		this.prefillRestaurantIds = restaurantIds;
		this.clearViewedJobAction();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	private async applyHistoryFilters(next: TableFilterParams): Promise<void> {
		this.isLoadingHistory = true;
		try {
			await this.fetchJobHistory(next);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isLoadingHistory = false;
		}
	}

	private async goToPreviousHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current > 1) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current - 1 });
		}
	}

	private async goToNextHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current < this.totalHistoryPages) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current + 1 });
		}
	}

	private async onHistorySortChange(columnName: string): Promise<void> {
		const nextDirection: 'asc' | 'desc' = this.historyFilters.sortBy === columnName
			? (this.historyFilters.sortDirection === 'asc' ? 'desc' : 'asc')
			: 'asc';
		await this.applyHistoryFilters({
			...this.historyFilters,
			page: 1,
			sortBy: columnName,
			sortDirection: nextDirection
		});
	}
}
